import { ActionButton, AlertDialog, DialogContainer, DialogTrigger, Flex, Grid, View } from '@adobe/react-spectrum';
import { encode } from 'html-entities';
import { FPATree } from '../../../components/protected/FPATree/FPATree';
import styles from './Finder.module.css';
import { CACHE_KEYS, saveToCache, usePreloadAssets } from '../../../hooks/UsePreloadAssets';
import { ListRegisterUtils } from '../../../utils/ListRegisterUtils';
import { SoapUtils } from '../../../utils/SoapUtils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDependency } from '../../../contexts/DependencyProvider';
import { FPAData, FPADataFactory, FPADataTypes, IFPAData } from '../../../infra/protected/FPA/FPAData';
import { FinderDetails } from './components/FinderDetails';
import { FinderIcon, SearchIcon } from '../../../components/protected/Icons/IconsLib';
import { useLocation, useSearchParams } from 'react-router-dom';
import { FinderDetailsMobile } from './components/FinderDetailsMobile';
import { useViewInfo } from '../../../hooks/UseViewInfo';
import { useDeviceDetection } from '../../../hooks/UseDeviceDetection';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../state/store';
import FilterIcon from '@spectrum-icons/workflow/Filter';

import { 
  changePageOffsetOfItems,
  closeContextMode,
  closeDetails,
  doSearch,
  getContextPath,
  hideSearch,
  loadFinder,
  loadFolderTypes, 
  loadTreeData, 
  moveToParent, 
  nextSelection, 
  openContextMode, 
  openDetails, 
  previousSelection, 
  removeItemFromTree, 
  resetFinder, 
  resetSearch, 
  setMappings, 
  setSearchFilter, 
  setSelectedIndexAsSelectedItem, 
  setSelectedItem, 
  setSelectedItemAsSelectedIndex, 
  setTreeFocus, 
  showGlobalSearch, 
  showSearch, 
  showSearchedSelected, 
  unloadTreeData, 
  updateFilters
} from '../../../state/Finder/finderSlice';
import { InPlaceSearch } from '../../../components/protected/FPATree/components/InPlaceSearch/InPlaceSearch';
import { FPAFiltersDialog } from '../../../components/protected/Dialogs/FPAFiltersDialog';
import { AtollonEvent, publish, subscribe, unsubscribe } from '../../../infra/events/Events';
import { updateAppInfo } from '../../../state/userWebLayout/userWebLayoutSlice';
import FolderAddToIcon from '@spectrum-icons/workflow/FolderAddTo';
import { addKey, useKeyboardShortcuts } from '../../../hooks/UseKeyboardShortcuts';
import { GetContextPathResponse } from '../../../services/soap/project/responses/GetContextPathResponse';
import ConstantUtils from '../../../utils/ConstantUtils';
import { useTranslation } from 'react-i18next';
import { ContextNewDialog, ContextNewDialogMode } from '../../../components/protected/Context/ContextNewDialog';
import { DataProvider } from '../../../components/protected/data-components/Contexts/DataContext';

import { DeleteFolderRequest } from '../../../services/soap/project/requests/DeleteFolderRequest';
import { DeleteProjectRequest } from '../../../services/soap/project/requests/DeleteProjectRequest';
import { DeleteActivityRequest } from '../../../services/soap/project/requests/DeleteActivityRequest';
import { ToastQueue } from '@adobe/react-spectrum';
import { ContextMoveDialog } from '../../../components/protected/Context/ContextMoveDialog';

interface MoveProperties {
  showActivities: boolean;
  showProjects: boolean;
  showFolders: boolean;
  relatedToActivityType?: number;
  fpaTypeCategories?: string;
  selectedItemType: FPADataTypes;
}

export function Finder () {  
  const treeData = useSelector((state: RootState) => state.finder.treeData);
  const treeFocus = useSelector((state: RootState) => state.finder.treeFocus);
  const selectedItem = useSelector((state: RootState) => state.finder.selectedItem);
  const showOverlay = useSelector((state: RootState) => state.finder.showOverlay);
  const showGlobalFinder = useSelector((state: RootState) => state.finder.showGlobalFinder);
  const searchResults = useSelector((state: RootState) => state.finder.searchData);
  const searchFilter = useSelector((state: RootState) => state.finder.searchFilter);
  const inContextMode = useSelector((state: RootState) => state.finder.inContextMode);
  const lastOpenedItem = useSelector((state: RootState) => state.finder.lastOpenedItem);
  const dataStore = useSelector((state: RootState) => state.userWebLayout.appInfos);
  const search_result_offset = useSelector((state: RootState) => state.finder.search_result_offset);
  const search_result_total = useSelector((state: RootState) => state.finder.search_result_total);
  const isSearching = useSelector((state: RootState) => state.finder.isSearching);
  const showDetails = useSelector((state: RootState) => state.finder.showDetails);
  const backupDataLength = useSelector((state: RootState) => state.finder.backupData.length);
  const { t } = useTranslation();
  const [mode, setMode] = useState<ContextNewDialogMode>('new');
  const [showAdd, setShowAdd] = useState(false);
  const [isMove, setIsMove] = useState(false);

  const [moveProperties, setMoveProperties] = useState<MoveProperties>({
    showActivities: false,
    showProjects: false,
    showFolders: false,
    relatedToActivityType: 0,
    selectedItemType: FPADataTypes.NONE
  });
  const dispatch = useDispatch<AppDispatch>();
  const dataRef = useRef<any>({});
  const formGuardsRef = useRef<Map<number, { isDirty: boolean; pendingCount: number }>>(new Map());
  const pendingGuardActionRef = useRef<null | (() => void)>(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [unsavedDialogPendingCount, setUnsavedDialogPendingCount] = useState(0);
  const searchCallRef = useRef<any>(null);
  const { registers, folderTypes, folderStatusMapping, projectStatusMapping, activityStatusMapping } = usePreloadAssets();
  const { isMobile } = useViewInfo();
  const deviceInfo = useDeviceDetection();

  // Resizable pane state
  const [sidebarWidth, setSidebarWidth] = useState<number>(25); // Default 25% (1fr out of 4fr total)
  const [isResizing, setIsResizing] = useState(false);
  // Drawer (tablet) resizable width state (in pixels)
  const [drawerWidth, setDrawerWidth] = useState<number>(600);
  const [isDrawerResizing, setIsDrawerResizing] = useState(false);

  // Tablet portrait slider state
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const isTabletPortrait = deviceInfo.isTablet && deviceInfo.orientation === 'portrait';


  const { featureService, projectService, store } = useDependency();
  const [params] = useSearchParams();
  const { state } = useLocation();
  const appKeyRef = useRef<string>(state.appKey);
  const dataStoreRef = useRef<any>(dataStore);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  const _t = (key: string) => t(key, { ns: 'finder' });
  const showSelectedItem = () => {
    if(timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    timerRef.current = setTimeout(() => {
      dispatch(setSelectedItemAsSelectedIndex());
      timerRef.current = null;
    }, 300);
  }

  const handleNewContextOpenChanged = useCallback((isOpen: boolean, mode: ContextNewDialogMode = 'new', isMove: boolean = false) => {
    dispatch(setTreeFocus(!isOpen && !isMove));
    setShowAdd(isOpen);
    setMode(mode);
    setIsMove(isMove);
  }, [dispatch]);

  const handleCreateContextApp = useCallback(
    (event: AtollonEvent) => {
      if(event.detail.isCopy){
        handleNewContextOpenChanged(true, 'copy');
      }
      else{
        if(event.detail.selectedItem){
          var target_type: FPADataTypes = FPADataTypes.NONE;
          if(event.detail.selectedItem.type === FPADataTypes.ACTIVITY)
            target_type = FPADataTypes.PROJECT;
          else if(event.detail.selectedItem.type === FPADataTypes.PROJECT)
            target_type = FPADataTypes.FOLDER;
          
          setMoveProperties({
            showActivities: false,
            showProjects: event.detail.selectedItem.type === FPADataTypes.ACTIVITY,
            showFolders: event.detail.selectedItem.type === FPADataTypes.PROJECT || event.detail.selectedItem.type === FPADataTypes.ACTIVITY,
            selectedItemType: target_type
          });
          handleNewContextOpenChanged(false, 'move', true);
        }
      }
    },
    [handleNewContextOpenChanged]
  );


  useEffect(() => {
    subscribe(AtollonEvent.CREATE_CONTEXT_APP, handleCreateContextApp);
    return () => {
      unsubscribe(AtollonEvent.CREATE_CONTEXT_APP, handleCreateContextApp);
    };

  }, [handleCreateContextApp]);

  const handleFormGuardChanged = useCallback((event: AtollonEvent) => {
    const detail = event?.detail ?? {};
    const contextId = detail.contextId;
    if (contextId === undefined || contextId === null) return;

    const id = +contextId;
    const isDirty = !!detail.isDirty;
    const pendingCount = +(detail.pendingCount ?? 0);

    if (!isDirty && pendingCount <= 0) {
      formGuardsRef.current.delete(id);
      return;
    }

    formGuardsRef.current.set(id, { isDirty, pendingCount });
  }, []);

  useEffect(() => {
    subscribe(AtollonEvent.FORM_GUARD_CHANGED, handleFormGuardChanged);
    return () => {
      unsubscribe(AtollonEvent.FORM_GUARD_CHANGED, handleFormGuardChanged);
    };
  }, [handleFormGuardChanged]);

  const confirmLeaveIfNeeded = useCallback((action: () => void) => {
    const currentContextId = selectedItem?.id;
    if (!currentContextId) {
      action();
      return;
    }

    const guard = formGuardsRef.current.get(+currentContextId);
    if (guard && (guard.isDirty || guard.pendingCount > 0)) {
      pendingGuardActionRef.current = action;
      setUnsavedDialogPendingCount(guard.pendingCount);
      setShowUnsavedDialog(true);
      return;
    }

    action();
  }, [selectedItem]);


  useKeyboardShortcuts([
    ...addKey('f', {ctrl: true}, () =>{ 
      if(selectedItem && selectedItem.type !== FPADataTypes.ITEM_TYPE){
        if(selectedItem.type === FPADataTypes.FOLDER_TYPE || selectedItem.count > 0)
          handleItemSearchOpened(selectedItem);
      }else
        handleOpenGlobalFinderSearch()
    }),
    ...addKey('ArrowUp', {}, () =>{ 
      dispatch(previousSelection());
      showSelectedItem();
    }),
    ...addKey('ArrowUp', {ctrl: true}, () => {
      dispatch(moveToParent());
    }),
    ...addKey('ArrowDown', {ctrl: true}, () => {
      if(selectedItem)
        handleItemSelected(selectedItem, true);
    }),
    ...addKey('ArrowDown', {}, () => {
      dispatch(nextSelection());
      showSelectedItem();
    }),
    ...addKey('ArrowLeft', {}, () => {
      if(selectedItem 
        && selectedItem.type !== FPADataTypes.ITEM_TYPE
      ){
        if(selectedItem.type === FPADataTypes.FOLDER_TYPE || selectedItem.count > 0)
          handleItemClosed(selectedItem);
      }
    }),
    ...addKey('ArrowRight', {}, () => {
      if(selectedItem 
        && selectedItem.type !== FPADataTypes.ITEM_TYPE
      ){
        if(selectedItem.type === FPADataTypes.FOLDER_TYPE || selectedItem.count > 0)
          handleItemOpened(selectedItem);
      }
    }),
    ...addKey('Enter', {}, () =>{ 
      if(selectedItem)
        handleItemSelected(selectedItem, true);
    }),
    ...addKey('Escape', {}, () => {
      if(showOverlay){ 
        handleItemSearchClosed(true);
        return;
      }
      handleShowAll();
    }),
  ],treeFocus);
  
  useEffect(() => {
    dataRef.current = { treeData, selectedItem, showDetails, inContextMode, lastOpenedItem, sidebarWidth, drawerWidth};
  },[treeData, selectedItem, showDetails, inContextMode, lastOpenedItem, sidebarWidth, drawerWidth]);

  useEffect(() => {
    if(Object.keys(folderTypes).length === 0) return;

    if(dataStoreRef.current[appKeyRef.current]?.treeData){
      try {
        // Handle both old (non-serialized) and new (serialized) data formats
        const treeData = typeof dataStoreRef.current[appKeyRef.current].treeData === 'string' 
          ? JSON.parse(dataStoreRef.current[appKeyRef.current].treeData) 
          : dataStoreRef.current[appKeyRef.current].treeData;
        
        const selectedItem = dataStoreRef.current[appKeyRef.current].selectedItem
          ? (typeof dataStoreRef.current[appKeyRef.current].selectedItem === 'string'
              ? JSON.parse(dataStoreRef.current[appKeyRef.current].selectedItem)
              : dataStoreRef.current[appKeyRef.current].selectedItem)
          : null;
        
        const lastOpenedItem = dataStoreRef.current[appKeyRef.current].lastOpenedItem
          ? (typeof dataStoreRef.current[appKeyRef.current].lastOpenedItem === 'string'
              ? JSON.parse(dataStoreRef.current[appKeyRef.current].lastOpenedItem)
              : dataStoreRef.current[appKeyRef.current].lastOpenedItem)
          : null;

        if(treeData && treeData.length > 0) {
          dispatch(loadFinder({
            treeData: treeData as IFPAData[],
            selectedItem: selectedItem,
            showDetails: dataStoreRef.current[appKeyRef.current].showDetails,
            inContextMode: dataStoreRef.current[appKeyRef.current].inContextMode,
            lastOpenedItem: lastOpenedItem
          }));
        }
      } catch (error) {
        console.error('Error loading finder data from store:', error);
        // If there's an error parsing old data, just skip it and load fresh
      }
    } else if(params.has('id')){
      dispatch(getContextPath({
        api: projectService,
        contextId: +params.get('id')!,
        sessionId: store.SessionId,
        server: store.Server,
        isForSearch: false
      })).then((tmp) => {
        var item = tmp.payload as GetContextPathResponse;
        if(item.count === 0) return;
        var t_item = FPADataFactory.createFromContextItem(item.LIST[0].CONTEXT, folderStatusMapping, projectStatusMapping, activityStatusMapping) as IFPAData;
        var p_item = t_item;
        for(let i = 1; i < item.LIST.length; i++){
          var new_p_item = FPADataFactory.createFromContextItem(item.LIST[i].CONTEXT, folderStatusMapping, projectStatusMapping, activityStatusMapping) as IFPAData;
          p_item.parent_id = new_p_item.id;
          p_item.parent_type = new_p_item.type;
          p_item.parent = new_p_item;
          p_item = new_p_item;
        }
        dispatch(loadTreeData({
          api: projectService, 
          itemType: t_item.type, 
          itemId: t_item.id, 
          sessionId: store.SessionId, 
          server: store.Server,
          offset: 0,
          limit: ConstantUtils.LIST_FPA_PAGE_SIZE
        })).then((tmp1) => {
          dispatch(setSelectedIndexAsSelectedItem());
          dispatch(openDetails());
        });
      });
    }      
    else
      dispatch(loadFolderTypes({folderTypes}));
    
    // dispatch(setSelectedIndex(0));

    return () => {
      if(searchCallRef.current) searchCallRef.current.abort();
      if(timerRef.current){ clearTimeout(timerRef.current); timerRef.current = null; }

      dispatch(updateAppInfo({ 
        key: appKeyRef.current, 
        data: {
          treeData: JSON.stringify(dataRef.current.treeData),
          selectedItem: JSON.stringify(dataRef.current.selectedItem),
          showDetails: dataRef.current.showDetails,
          inContextMode: dataRef.current.inContextMode,
          lastOpenedItem: JSON.stringify(dataRef.current.lastOpenedItem),
          sidebarWidth: dataRef.current.sidebarWidth,
          drawerWidth: dataRef.current.drawerWidth
        }
      }));
      dispatch(resetFinder());
    }
  },
  [dispatch, folderTypes, params, projectService, store.Server, store.SessionId]);

  // Update refs when values change
  useEffect(() => {
    appKeyRef.current = state.appKey;
    dataStoreRef.current = dataStore;
  }, [state.appKey, dataStore]);

  useEffect(() => {
    if(registers === undefined) return;
    var registry_key = `${ListRegisterUtils.Modules.Finder}_${ListRegisterUtils.Keys.Finder.FinderSettings}`;
    if(registers[registry_key] === undefined) return;
    const search_settings = (registers[registry_key]).value ? SoapUtils.parseXmlString((registers[registry_key]).value) : {};
    dispatch(setSearchFilter({...search_settings}));
  },[dispatch, registers]);
  
  useEffect(() => {
    if(folderStatusMapping === undefined || projectStatusMapping === undefined || activityStatusMapping === undefined) return;
    dispatch(setMappings({folderStatusMapping, projectStatusMapping, activityStatusMapping}));
  },[folderStatusMapping, projectStatusMapping, activityStatusMapping, dispatch]);

  const handleItemOpened = (item:IFPAData) => {    
    dispatch(setSelectedItemAsSelectedIndex());
    dispatch(loadTreeData({
      api: projectService, 
      itemType: item.type, 
      itemId: item.id, 
      sessionId: store.SessionId, 
      server: store.Server,
      offset: item.page_offset,
      limit: ConstantUtils.LIST_FPA_PAGE_SIZE
    })).then((tmp) => {
      dispatch(changePageOffsetOfItems({itemId: item.id, offset: item.page_offset + ConstantUtils.LIST_FPA_PAGE_SIZE}));
      if(item.parent_id && item.count === 0){
        
      }
    });
  }

  const handleItemClosed = (item:IFPAData) => {
    dispatch(unloadTreeData({itemId: item.id}));
  }

  const changeQueryStringId = (id:number) => {
    const url = new URL(window.location.href);
    url.searchParams.set('id', id+"");
    window.history.pushState(null, '', url.toString());
  }
  const handleItemSelected = (item:IFPAData, show_context:boolean = true) => {
    confirmLeaveIfNeeded(() => {
      dispatch(setSelectedItemAsSelectedIndex());
      dispatch(setSelectedItem({item, show_context}));
      dispatch(openDetails());
      if(show_context){
        changeQueryStringId(item.id);
          if(item.type === FPADataTypes.FOLDER_TYPE || item.count > 0)
            dispatch(loadTreeData({
              api: projectService, 
              itemType: item.type, 
              itemId: item.id, 
              sessionId: store.SessionId, 
              server: store.Server,
              offset: 0,
              limit: ConstantUtils.LIST_FPA_PAGE_SIZE
            })).then((tmp) => {
              dispatch(setSelectedIndexAsSelectedItem());
              dispatch(changePageOffsetOfItems({itemId: item.id, offset: ConstantUtils.LIST_FPA_PAGE_SIZE}));
            });
          else{
            dispatch(setSelectedIndexAsSelectedItem());
          }
      }
      publish(AtollonEvent.INFO_CHANGED, item.title);
    });
  }

  const handleOverlayClicked = () => {
    dispatch(hideSearch(true));
  }

  const handleItemSearching = (search:string, parent_id?:number, parent_type?:FPADataTypes, is_page:boolean = false) => {
    // Enhanced guard: prevent if already searching, or if we've reached the end
    if (is_page) {
      if (isSearching) return; // Prevent concurrent requests
      if (search_result_offset >= search_result_total && search_result_total > 0) return;
      if (searchResults.length >= search_result_total && search_result_total > 0) return;
    }

    if(searchCallRef.current) searchCallRef.current.abort();

    searchCallRef.current = dispatch(doSearch({
      api: projectService, 
      itemType: parent_type, 
      itemId: parent_id, 
      searchText: search,
      sessionId: store.SessionId, 
      server: store.Server,
      offset: is_page ? search_result_offset : 0,
      limit: ConstantUtils.LIST_FPA_PAGE_SIZE
    }));
  }

  const handleItemSearchOpened = (item:FPAData) => {
    dispatch(showSearch(item));
  }

  const handleItemSearchClosed = (isCancel: boolean) => {
    dispatch(hideSearch(isCancel));
  }

  const handleRootSearchedItemSelected = (item:any) => {
    confirmLeaveIfNeeded(() => {
      dispatch(hideSearch(false));
      dispatch(showSearchedSelected(item.item));
      dispatch(getContextPath({ 
        api: projectService, 
        contextId: item.contextId, 
        sessionId: store.SessionId, 
        server: store.Server,
        isForSearch: true
      })).then((tmp) => {
        dispatch(loadTreeData({
          api: projectService, 
          itemType: item.item.type, 
          itemId: item.item.id, 
          sessionId: store.SessionId, 
          server: store.Server,
          offset: 0,
          limit: ConstantUtils.LIST_FPA_PAGE_SIZE
        })).then((tmp1) => {
          dispatch(setSelectedIndexAsSelectedItem());
          dispatch(changePageOffsetOfItems({ itemId: item.item.id, offset: ConstantUtils.LIST_FPA_PAGE_SIZE }));
        });
      });
      publish(AtollonEvent.INFO_CHANGED, item.item.title);
    });
  }
  const handleSearchedItemSelected = (item:FPAData) => {
    confirmLeaveIfNeeded(() => {
      dispatch(showSearchedSelected(item));
      dispatch(openContextMode(item));
      dispatch(loadTreeData({
        api: projectService, 
        itemType: item.type, 
        itemId: item.id, 
        sessionId: store.SessionId, 
        server: store.Server,
        offset: 0,
        limit: ConstantUtils.LIST_FPA_PAGE_SIZE
      })).then(() => {
        dispatch(setSelectedIndexAsSelectedItem());
        dispatch(changePageOffsetOfItems({ itemId: item.id, offset: ConstantUtils.LIST_FPA_PAGE_SIZE }));
      });
      publish(AtollonEvent.INFO_CHANGED, item.title);
    });
  }

  const handleSearchReset = (item:FPAData) => {
    dispatch(resetSearch(item));
  }

  const handleRootSearch = (search:string, is_page:boolean = false) => {
    // Enhanced guard: prevent if already searching, or if we've reached the end
    if (is_page) {
      if (isSearching) return; // Prevent concurrent requests
      if (search_result_offset >= search_result_total && search_result_total > 0) return;
      if (searchResults.length >= search_result_total && search_result_total > 0) return;
    }
    
    if(searchCallRef.current) searchCallRef.current.abort();
    searchCallRef.current = dispatch(doSearch({
      api: projectService, 
      searchText: search,
      sessionId: store.SessionId, 
      server: store.Server,
      offset: is_page ? search_result_offset : 0,
      limit: ConstantUtils.LIST_FPA_PAGE_SIZE
    }));
  }

  const handleOpenGlobalFinderSearch = () => {
    dispatch(showGlobalSearch());
  }

  const handleShowAll = () => {
    dispatch(closeContextMode());
    dispatch(setSelectedIndexAsSelectedItem());
    
    if(backupDataLength === 0)
      dispatch(loadFolderTypes({folderTypes}));

    publish(AtollonEvent.INFO_CHANGED, '');
  }

  const handleGoBack = () => {
    confirmLeaveIfNeeded(() => dispatch(closeDetails()));
  }

  const handleLoadMore = (item:FPAData) => {
    if(item.page_offset >= item.count) return;
    dispatch(loadTreeData({
      api: projectService, 
      itemType: item.type, 
      itemId: item.id, 
      sessionId: store.SessionId, 
      server: store.Server,
      offset: item.page_offset,
      limit: ConstantUtils.LIST_FPA_PAGE_SIZE
    })).then((tmp) => {
      if(tmp.payload)
        dispatch(changePageOffsetOfItems({itemId: item.id, offset: item.page_offset + ConstantUtils.LIST_FPA_PAGE_SIZE}));
    });
  }

  const onApply = (filter:any) => {
    var registry_key = `${ListRegisterUtils.Modules.Finder}_${ListRegisterUtils.Keys.Finder.FinderSettings}`;
    registers[registry_key] = { ...registers[registry_key] , value: SoapUtils.JStoXML(filter)};

    dispatch(updateFilters({
      api: featureService, 
      sessionId: store.SessionId, 
      server: store.Server,
      id: registers[registry_key].id,
      moduleId: ListRegisterUtils.Modules.Finder,
      key: ListRegisterUtils.Keys.Finder.FinderSettings,
      level: registers[registry_key].level,
      value: encode(SoapUtils.JStoXML(filter)),
      assignTo: registers[registry_key].assignTo
    }));
    
    saveToCache(CACHE_KEYS.registers, registers);
  }

  const handleClickedDetails = () => {
    dispatch(setTreeFocus(false));
  }

  const handleClickedTree = () => {
    dispatch(setTreeFocus(true));
  }

  const handleDelete = async (item: FPAData) => {
    var success = false;
    switch(item.type) {
      case FPADataTypes.FOLDER:
          var folderResult = await projectService.deleteFolder(new DeleteFolderRequest(store.Server, store.SessionId, item.id));
          if(folderResult.EXCEPTION) {
              ToastQueue.negative(folderResult.EXCEPTION.message, { timeout: 2000 });
          } else {
              ToastQueue.positive(_t("folder deleted successfully"), { timeout: 2000 });
              success = true;
          }
          break;
      case FPADataTypes.PROJECT:
          var projectResult = await projectService.deleteProject(new DeleteProjectRequest(store.Server, store.SessionId, item.id));
          if(projectResult.EXCEPTION) {
              ToastQueue.negative(projectResult.EXCEPTION.message, { timeout: 2000 });
          } else {
              ToastQueue.positive(_t("project deleted successfully"), { timeout: 2000 });
              success = true;
          }
          break;
      case FPADataTypes.ACTIVITY:
          var activityResult = await projectService.deleteActivity(new DeleteActivityRequest(store.Server, store.SessionId, item.id));
          if(activityResult.EXCEPTION) {
              ToastQueue.negative(activityResult.EXCEPTION.message, { timeout: 2000 });
          } else {
              ToastQueue.positive(_t("activity deleted successfully"), { timeout: 2000 });
              success = true;
          }
          break;
      default:
          break;
    }
    if(success)
      dispatch(removeItemFromTree(item.id));
  }

  const handleMoveClose = (moved: boolean) => {
    if (moved) {
      //listProjectList.reload();
    }
    setIsMove(false);
  };

  // Tablet portrait slider handlers
  const handleSliderToggle = () => {
    setIsSliderOpen(!isSliderOpen);
  };

  const handleSliderClose = () => {
    setIsSliderOpen(false);
  };

  const handleItemSelectedWithSlider = (item: FPAData, show_context: boolean = true) => {
    // Close slider when item is selected in tablet portrait mode
    if (isTabletPortrait) {
      setIsSliderOpen(false);
    }
    handleItemSelected(item, show_context);
  };

  // Resizable pane handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    const containerWidth = window.innerWidth;
    const newWidth = (e.clientX / containerWidth) * 100;
    
    // Constrain between 15% and 60%
    if (newWidth >= 15 && newWidth <= 60) {
      setSidebarWidth(newWidth);
    }
  }, [isResizing]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isResizing) return;
    
    e.preventDefault();
    const containerWidth = window.innerWidth;
    const newWidth = (e.touches[0].clientX / containerWidth) * 100;
    
    // Constrain between 15% and 60%
    if (newWidth >= 15 && newWidth <= 60) {
      setSidebarWidth(newWidth);
    }
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
    }
  }, [isResizing]);

  const handleTouchEnd = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
    }
  }, [isResizing]);

  // Load saved sidebar/drawer width on mount
  useEffect(() => {
    if (dataStore[state.appKey]?.sidebarWidth) {
      setSidebarWidth(dataStore[state.appKey].sidebarWidth);
    }
    if (dataStore[state.appKey]?.drawerWidth) {
      setDrawerWidth(dataStore[state.appKey].drawerWidth);
    }
  }, [dataStore, state.appKey]);

  // Attach mouse and touch event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.body.style.touchAction = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.touchAction = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.touchAction = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Drawer resizer handlers (tablet portrait)
  const handleDrawerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDrawerResizing(true);
  };

  const handleDrawerTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    setIsDrawerResizing(true);
  };

  const handleDrawerMouseMove = useCallback((e: MouseEvent) => {
    if (!isDrawerResizing) return;
    const containerWidth = window.innerWidth;
    const calculated = containerWidth - e.clientX; // drawer anchored to right
    const minPx = 260;
    const maxPx = Math.min(600, Math.floor(containerWidth * 0.7));
    const next = Math.max(minPx, Math.min(maxPx, calculated));
    setDrawerWidth(next);
  }, [isDrawerResizing]);

  const handleDrawerTouchMove = useCallback((e: TouchEvent) => {
    if (!isDrawerResizing) return;
    e.preventDefault();
    const containerWidth = window.innerWidth;
    const clientX = e.touches[0].clientX;
    const calculated = containerWidth - clientX;
    const minPx = 260;
    const maxPx = Math.min(600, Math.floor(containerWidth * 0.7));
    const next = Math.max(minPx, Math.min(maxPx, calculated));
    setDrawerWidth(next);
  }, [isDrawerResizing]);

  const handleDrawerMouseUp = useCallback(() => {
    if (isDrawerResizing) setIsDrawerResizing(false);
  }, [isDrawerResizing]);

  const handleDrawerTouchEnd = useCallback(() => {
    if (isDrawerResizing) setIsDrawerResizing(false);
  }, [isDrawerResizing]);

  // Attach listeners for drawer resizing
  useEffect(() => {
    if (isDrawerResizing) {
      document.addEventListener('mousemove', handleDrawerMouseMove);
      document.addEventListener('mouseup', handleDrawerMouseUp);
      document.addEventListener('touchmove', handleDrawerTouchMove, { passive: false });
      document.addEventListener('touchend', handleDrawerTouchEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.body.style.touchAction = 'none';
    } else {
      document.removeEventListener('mousemove', handleDrawerMouseMove);
      document.removeEventListener('mouseup', handleDrawerMouseUp);
      document.removeEventListener('touchmove', handleDrawerTouchMove);
      document.removeEventListener('touchend', handleDrawerTouchEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.removeEventListener('mousemove', handleDrawerMouseMove);
      document.removeEventListener('mouseup', handleDrawerMouseUp);
      document.removeEventListener('touchmove', handleDrawerTouchMove);
      document.removeEventListener('touchend', handleDrawerTouchEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.touchAction = '';
    };
  }, [isDrawerResizing, handleDrawerMouseMove, handleDrawerMouseUp, handleDrawerTouchMove, handleDrawerTouchEnd]);

  // Auto-open slider in tablet portrait when no id is in querystring
  useEffect(() => {
    if (isTabletPortrait && !params.has('id')) {
      setIsSliderOpen(true);
    }
  }, [isTabletPortrait, params]);

  
  return (
    <>
      <DialogContainer
        onDismiss={() => {
          pendingGuardActionRef.current = null;
          setShowUnsavedDialog(false);
          setUnsavedDialogPendingCount(0);
        }}
      >
        {showUnsavedDialog && (
          <AlertDialog
            variant="confirmation"
            title="Unsaved changes"
            primaryActionLabel="Leave"
            cancelLabel="Stay"
            onPrimaryAction={() => {
              setShowUnsavedDialog(false);
              const action = pendingGuardActionRef.current;
              pendingGuardActionRef.current = null;
              setUnsavedDialogPendingCount(0);
              if (action) action();
            }}
            onCancel={() => {
              pendingGuardActionRef.current = null;
              setShowUnsavedDialog(false);
              setUnsavedDialogPendingCount(0);
            }}
          >
            {unsavedDialogPendingCount > 0
              ? 'Saving in progress. You have unsaved changes. Are you sure you want to leave?'
              : 'You have unsaved changes. Are you sure you want to leave?'}
          </AlertDialog>
        )}
      </DialogContainer>
      {showOverlay && (<span className={styles.overlay} onClick={handleOverlayClicked}>
      </span>)}
        <DataProvider>
          <Grid areas={ isTabletPortrait ? { base:['content'], M:['content'] } : { base:['sidebar'], M:['sidebar resizer content'] } } 
                columns={ isTabletPortrait ? { base:['1fr'], M:['1fr'] } : { base:['1fr'], M: [`${sidebarWidth}%`, 'auto', `${100 - sidebarWidth}%`] } } 
                UNSAFE_className={styles.appContainer} rows={['auto']} height="calc(100vh - 60px)">
              {/* Tablet portrait mode header */}
              {isTabletPortrait && (
                <View gridArea="content" backgroundColor={'static-white'} paddingTop={'size-100'}>
                  <Flex direction="row" gap="size-100" justifyContent={'space-between'} alignItems={'center'} UNSAFE_style={{ paddingLeft: 'var(--spectrum-global-dimension-size-200)', paddingRight: 'var(--spectrum-global-dimension-size-200)' }}>
                    <View UNSAFE_className={styles.contextText} flex UNSAFE_style={{ minWidth: 0 }}>
                      {_t('context')}
                    </View>
                  </Flex>
                </View>
              )}
              
              <View gridArea="sidebar" backgroundColor={'static-white'} paddingTop={'size-100'} isHidden={ isTabletPortrait ? { base: true, M: true } : { base: false, M: false } }>
                <Flex direction="column" gap="size-100" height="100%" UNSAFE_className={styles.treeHolder}>
                  {showGlobalFinder && (<Flex direction="row" UNSAFE_className={styles.searchHeader}>
                      <View flex>
                        <InPlaceSearch closeSearch={handleItemSearchClosed}
                          doSearch={handleRootSearch}
                          onSearchSelected={handleRootSearchedItemSelected}
                          items={searchResults}
                          />
                      </View>
                    </Flex>)}
                    {(!isMobile || !showDetails) && !showGlobalFinder && (
                        <Flex direction="row" gap="size-10" justifyContent={'space-between'} UNSAFE_className={styles.contextHeader}>
                        <View UNSAFE_className={styles.contextText} flex UNSAFE_style={{ minWidth: 0 }}>
                          {_t('context')}
                        </View>
                        {inContextMode && (<View>
                            <ActionButton UNSAFE_className={styles.addNewButton} onPress={() => handleNewContextOpenChanged(true, 'new')}>
                              {_t('add new')}
                            </ActionButton>
                          <ActionButton isQuiet 
                            UNSAFE_className={styles.actionButton} 
                            onPress={handleShowAll}
                            >
                                {_t('show all')}
                            </ActionButton>
                        </View>)}
                        {!inContextMode && (<View>
                          <ActionButton UNSAFE_className={styles.addNewButton} onPress={() => handleNewContextOpenChanged(true, 'new')}>
                              {_t('add new')}
                          </ActionButton>
                          <ActionButton isQuiet 
                            UNSAFE_className={styles.actionButton} 
                            onPress={handleOpenGlobalFinderSearch}
                            >
                              <span>
                                <SearchIcon size={20} />
                              </span>
                            </ActionButton>
                          <DialogTrigger>
                            <ActionButton isQuiet>
                              <span className={styles.filterIcon}>
                                <FilterIcon size={'M'} />
                              </span>
                            </ActionButton>
                            {(close) => (<FPAFiltersDialog 
                                            filter={searchFilter} 
                                            onClose={close} 
                                            onApply={onApply}
                                            />)}
                          </DialogTrigger>
                        </View>)}
                    </Flex>)}                  
                    {(isMobile && showDetails) && (<Flex direction="row" gap="size-10" justifyContent={'end'}>
                        <View>
                          <ActionButton isQuiet onPress={handleGoBack} >
                            <FolderAddToIcon />
                          </ActionButton>
                        </View>
                    </Flex>)}                  
                    {(!isMobile || !showDetails) && (
                      <View height={'100%'}>
                        <div onClick={handleClickedTree} onFocus={handleClickedTree}>
                          <FPATree 
                            treeData={treeData}
                            onItemOpened={handleItemOpened}
                            onItemClosed={handleItemClosed}
                            onItemSelected={handleItemSelectedWithSlider}
                            onItemSearching={handleItemSearching}
                            onItemSearchOpened={handleItemSearchOpened}
                            onItemSearchClosed={handleItemSearchClosed}
                            onSearchedItemSelected={handleSearchedItemSelected}
                            onItemSearchReset={handleSearchReset}
                            onItemLoadMore={handleLoadMore}
                            />
                        </div>
                      </View>)}
                    {(showDetails && isMobile) && (<View height={'100%'} isHidden={ { base: false, M: true } }>
                      <FinderDetailsMobile 
                          selectedItem={selectedItem} 
                          onClicked={handleClickedDetails} 
                          onDelete={handleDelete}
                          />
                    </View>)}
                </Flex>
              </View>
              {/* Resizer handle for desktop/tablet */}
              <View 
                gridArea="resizer" 
                isHidden={ { base: true, M: false } }
                UNSAFE_className={styles.resizer}
              >
                <div 
                  className={styles.resizerHandle}
                  onMouseDown={handleMouseDown}
                  onTouchStart={handleTouchStart}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize sidebar"
                />
              </View>
              <View gridArea="content" height={'100%'} isHidden={ isTabletPortrait ? { base: false, M: false } : { base: true, M: false } }>
                <Flex direction="column" 
                      gap="size-100" 
                      height="100%" 
                      alignItems={'center'} 
                      justifyContent={'start'}
                      UNSAFE_className={styles.detailsHolder}
                      >
                          <FinderDetails 
                              onClicked={handleClickedDetails} 
                              selectedItem={selectedItem} 
                              onDelete={handleDelete}
                              />
                </Flex>
              </View>
          </Grid>
          
          {/* Floating toggle button (always visible on tablet portrait) */}
          {isTabletPortrait && !isSliderOpen && (
            <div className={styles.floatingFab}>
              <ActionButton isQuiet onPress={handleSliderToggle}>
                <FinderIcon size={24} color="var(--primary-50)" />
              </ActionButton>
            </div>
          )}

          {/* Tablet portrait slider drawer */}
          {isTabletPortrait && (
            <>
              {/* Background overlay */}
              {isSliderOpen && (
                <div className={styles.sliderOverlay} onClick={handleSliderClose} />
              )}
              
              {/* Slider drawer */}
              <div className={`${styles.sliderDrawer} ${isSliderOpen ? styles.sliderOpen : ''}`} style={{ width: `${drawerWidth}px` }}>
                <div 
                  className={styles.drawerResizer}
                  onMouseDown={handleDrawerMouseDown}
                  onTouchStart={handleDrawerTouchStart}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize drawer"
                />
                <View backgroundColor={'static-white'} height="100%" paddingTop={'size-100'}>
                  <Flex direction="column" gap="size-100" height="100%" UNSAFE_className={styles.treeHolder}>
                    {showGlobalFinder && (<Flex direction="row" UNSAFE_className={styles.searchHeader}>
                        <View flex>
                          <InPlaceSearch closeSearch={handleItemSearchClosed}
                            doSearch={handleRootSearch}
                            onSearchSelected={handleRootSearchedItemSelected}
                            items={searchResults}
                            />
                        </View>
                      </Flex>)}
                      {!showGlobalFinder && (<Flex direction="row" gap="size-10" justifyContent={'space-between'} UNSAFE_className={styles.contextHeader}>
                          <View UNSAFE_className={styles.contextText} flex UNSAFE_style={{ minWidth: 0 }}>
                            {_t('context')}
                          </View>
                          {inContextMode && (<View>
                              <ActionButton UNSAFE_className={styles.addNewButton} onPress={() => handleNewContextOpenChanged(true, 'new')}>
                                {_t('add new')}
                              </ActionButton>
                            <ActionButton isQuiet 
                              UNSAFE_className={styles.actionButton} 
                              onPress={handleShowAll}
                              >
                                  {_t('show all')}
                              </ActionButton>
                          </View>)}
                          {!inContextMode && (<View>
                            <ActionButton UNSAFE_className={styles.addNewButton} onPress={() => handleNewContextOpenChanged(true, 'new')}>
                                {_t('add new')}
                            </ActionButton>
                            <ActionButton isQuiet 
                              UNSAFE_className={styles.actionButton} 
                              onPress={handleOpenGlobalFinderSearch}
                              >
                              <span>
                                <SearchIcon size={20} />
                              </span>
                            </ActionButton>
                            <DialogTrigger>
                              <ActionButton isQuiet>
                                <span className={styles.filterIcon}>
                                  <FilterIcon size={'M'} />
                                </span>
                              </ActionButton>
                              {(close) => (<FPAFiltersDialog 
                                              filter={searchFilter} 
                                              onClose={close} 
                                              onApply={onApply}
                                              />)}
                            </DialogTrigger>
                          </View>)}
                      </Flex>)}
                      
                      <View height={'100%'}>
                        <div onClick={handleClickedTree} onFocus={handleClickedTree}>
                          <FPATree 
                            treeData={treeData}
                            onItemOpened={handleItemOpened}
                            onItemClosed={handleItemClosed}
                            onItemSelected={handleItemSelectedWithSlider}
                            onItemSearching={handleItemSearching}
                            onItemSearchOpened={handleItemSearchOpened}
                            onItemSearchClosed={handleItemSearchClosed}
                            onSearchedItemSelected={handleSearchedItemSelected}
                            onItemSearchReset={handleSearchReset}
                            onItemLoadMore={handleLoadMore}
                            />
                        </div>
                      </View>
                  </Flex>
                </View>
              </div>
            </>
          )}
          
          <DialogContainer onDismiss={() => handleNewContextOpenChanged(false)}>
            {showAdd && (<ContextNewDialog onClose={() => handleNewContextOpenChanged(false)} 
                            parentItem={selectedItem ? (selectedItem.type === FPADataTypes.FOLDER || selectedItem.type === FPADataTypes.PROJECT || selectedItem.type === FPADataTypes.ACTIVITY ? selectedItem : null) : null} 
                            additionalMode={false} 
                            mode={mode} 
                            onOpeningNewContext={() => setIsSliderOpen(false)}
                            />)}
            {isMove && (<ContextMoveDialog 
                          onClose={handleMoveClose} 
                          parentItem={null} 
                          selectedItem={selectedItem} 
                          showActivities={moveProperties.showActivities} 
                          showProjects={moveProperties.showProjects} 
                          showFolders={moveProperties.showFolders} 
                          relatedToActivityType={moveProperties.relatedToActivityType} 
                          selectedItemType={moveProperties.selectedItemType} />)}
          </DialogContainer>
          
        </DataProvider>
    </>
  );
}
